/**
 * Layout — アプリ共通レイアウト
 * - Mobile: ヘッダー + コンテンツ + BottomNav
 * - Desktop (>=768px): Sidebar + ヘッダー + コンテンツ (BottomNav 非表示 #23)
 * - 種別切替 (cert-type-switcher) を全画面共通ヘッダーに配置 (FR-02)
 */

import { useContext } from 'react'
import { Outlet } from 'react-router-dom'
import BottomNav from './BottomNav'
import Sidebar from './Sidebar'
import Icon from './Icon'
import { AuthContext } from '../contexts/AuthContext'
import type { CertificationType } from '../api/types'
import { updateCertType } from '../api'

const CERT_LABELS: Record<CertificationType, string> = {
  first_class: '第一種',
  second_class: '第二種',
}

export default function Layout() {
  const { user, setUser } = useContext(AuthContext)

  const handleCertSwitch = async () => {
    if (!user) return
    const next: CertificationType =
      user.current_cert_type === 'first_class' ? 'second_class' : 'first_class'
    try {
      const updated = await updateCertType({ certification_type: next })
      if (updated) setUser(updated)
    } catch {
      // エラーは無視（切替失敗はトースト等で通知する拡張ポイント）
    }
  }

  return (
    <div className="flex min-h-screen bg-base">
      {/* Desktop サイドバー — md 以上で表示 (#23) */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* メインエリア */}
      <div className="flex min-h-screen flex-1 flex-col md:ml-[220px]">
        {/* 共通ヘッダー */}
        <header
          data-testid="header"
          className="sticky top-0 z-40 flex items-center gap-2 border-b border-border bg-surface/90 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-surface/80"
        >
          <button
            data-testid="menu-button"
            aria-label="メニュー"
            className="inline-flex h-10 w-10 items-center justify-center rounded-sm text-text-secondary transition-colors hover:bg-surface-alt md:hidden"
          >
            <Icon name="menu" size={20} />
          </button>

          <span className="flex-1 text-base font-bold tracking-tight text-text-primary">
            衛生管理者対策
          </span>

          {/* 種別切替 (FR-02) */}
          {user?.current_cert_type && (
            <button
              data-testid="cert-type-switcher"
              onClick={handleCertSwitch}
              aria-label={`現在: ${CERT_LABELS[user.current_cert_type]}。クリックで切替`}
              className="inline-flex items-center gap-1 rounded-pill border border-border bg-accent-subtle px-2.5 py-1.5 text-[13px] font-semibold text-accent transition-colors hover:border-accent"
            >
              <span>{CERT_LABELS[user.current_cert_type]}</span>
              <Icon name="chevron-down" size={14} />
            </button>
          )}
        </header>

        {/* ページコンテンツ */}
        <main className="flex-1 pb-[calc(var(--bottom-nav-height)+16px)] md:pb-4">
          <Outlet />
        </main>

        {/* Mobile ボトムナビ */}
        <div className="md:hidden">
          <BottomNav />
        </div>
      </div>
    </div>
  )
}
